'use client';

// Register guest login actions
import './GuestLoginActions';

import { LogIn, Shield, Sparkles, User, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { ContentRegistry } from '../../registries';
import { ContentItemProps } from '../../registries/ContentRegistry';

const GuestLoginCard: React.FC<ContentItemProps> = ({ content, variant = 'card', onAction, className }) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const isMinimal = variant === 'minimal';
  const isExpanded = variant === 'expanded';

  // Check if card was dismissed in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem(`dismissed_guest_login_${content.id}`);
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, [content.id]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    sessionStorage.setItem(`dismissed_guest_login_${content.id}`, 'true');
    setIsDismissed(true);
    onAction?.('dismiss', content);
  };

  const handleLogin = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAction?.('login', content);
  };

  const handleInspect = () => {
    onAction?.('inspect', content);
  };

  if (isDismissed) {
    return null;
  }

  // Minimal variant for compact display
  if (isMinimal) {
    return (
      <div
        className={`inspect-theme-bg-elevated rounded-lg p-4 cursor-pointer hover:inspect-theme-shadow transition-all ${className}`}
        onClick={handleInspect}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="inspect-theme-bg-primary-light p-2 rounded-full">
              <User className="w-4 h-4 inspect-theme-text-primary" />
            </div>
            <div>
              <h4 className="inspect-theme-text font-medium text-sm">{content.title}</h4>
              <p className="inspect-theme-text-muted text-xs mt-0.5">Click to personalize</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="inspect-theme-text-muted hover:inspect-theme-text p-1 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Full card display
  return (
    <article
      className={`inspect-theme-bg-elevated rounded-xl overflow-hidden hover:inspect-theme-shadow-lg transition-all duration-300 ${className}`}
    >
      {/* Gradient Header */}
      <div className="inspect-theme-gradient-accent p-6 text-white relative">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full">
            <Sparkles className="w-6 h-6" />
          </div>
          <h3 className="text-xl font-semibold">{content.title}</h3>
        </div>

        <p className="text-white/90 text-sm leading-relaxed">
          {content.description ||
            'Sign in to unlock personalized content, save your favorites, and let this website adapt to your interests.'}
        </p>
      </div>

      {/* Benefits Section */}
      <div className="p-6 space-y-4">
        <div className="space-y-3">
          {/* Benefit 1 */}
          <div className="flex items-start gap-3">
            <div className="inspect-theme-bg-primary-light p-2 rounded-lg mt-0.5">
              <User className="w-4 h-4 inspect-theme-text-primary" />
            </div>
            <div>
              <h4 className="inspect-theme-text font-medium text-sm mb-0.5">Personalized Experience</h4>
              <p className="inspect-theme-text-muted text-xs">
                Content tailored to your interests and preferences
              </p>
            </div>
          </div>

          {/* Benefit 2 */}
          <div className="flex items-start gap-3">
            <div className="inspect-theme-bg-primary-light p-2 rounded-lg mt-0.5">
              <Shield className="w-4 h-4 inspect-theme-text-primary" />
            </div>
            <div>
              <h4 className="inspect-theme-text font-medium text-sm mb-0.5">Secure & Private</h4>
              <p className="inspect-theme-text-muted text-xs">
                Your data is protected with enterprise-grade security
              </p>
            </div>
          </div>

          {/* Benefit 3 */}
          <div className="flex items-start gap-3">
            <div className="inspect-theme-bg-primary-light p-2 rounded-lg mt-0.5">
              <Sparkles className="w-4 h-4 inspect-theme-text-primary" />
            </div>
            <div>
              <h4 className="inspect-theme-text font-medium text-sm mb-0.5">Smart Recommendations</h4>
              <p className="inspect-theme-text-muted text-xs">
                Discover content you&apos;ll love based on your activity
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={handleLogin}
            className="flex-1 inspect-theme-button-primary px-4 py-2.5 rounded-lg font-medium text-sm transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            Sign In
          </button>
          <button
            onClick={handleInspect}
            className="flex-1 inspect-theme-button-secondary px-4 py-2.5 rounded-lg font-medium text-sm transition-all hover:scale-[1.02]"
          >
            Learn More
          </button>
        </div>

        {/* Privacy Note */}
        <p className="text-center inspect-theme-text-muted text-xs pt-2">
          No credit card required • Cancel anytime
        </p>
      </div>
    </article>
  );
};

// Auto-register with ContentRegistry
ContentRegistry.register('guest-login', GuestLoginCard);

export default GuestLoginCard;
